import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ymd } from '@/components/ui/PeriodPicker';
import { GST_STATE_MAP } from '@/lib/validation';
import { tradeCalc } from '@/lib/tradeCalc';
import { tradeSetup, type TradeInput } from '@/api/accountsBooks';
import { ChipTag, F, FormFooter, FormTitle, Msgs, Posting, ReadBox, Sec } from './voucherFormParts';
import { curBal, dd, dayName, inr, r2 } from './voucherFormat';
import { Grid, ItemsTable, NewRefTable, PartyDetails } from './tradeParts';
import { addDays, fromItems, newLine, useSaveTrade, type Line } from './tradeHelpers';
import type { FormProps } from './useVoucherForm';

/* Sales — laid out like Tally's Sales voucher (F8, item invoice), as the prototype:
   Invoice no. → Party A/c (GSTIN, place of supply) → Sales ledger → items → output GST →
   Party Details → e-Invoice and e-Way Bill → Bill-wise Details (New Ref = this invoice) →
   Narration → Posting. */

const PD_FIELDS: [string, string, number?][] = [
  ['billTo', 'Buyer (Bill to) address', 2], ['shipTo', 'Consignee (Ship to)', 2], ['shipAddr', 'Consignee address', 2],
  ['dispatchDoc', 'Dispatch Doc No.'], ['through', 'Dispatched through'], ['destination', 'Destination'],
  ['terms', 'Mode / Terms of payment'], ['otherRef', 'Other references'], ['delivery', 'Terms of delivery'],
];

interface State {
  date: string; party: number | ''; ledger: number | ''; order: string; orderDate: string; days: number; roundOff: boolean;
  lines: Line[]; irn: string; ack: string; ewb: string; vehicle: string; km: number; narr: string; pd: Record<string, string>;
}

export function SalesForm({ voucher, ledgers, nextNo, onClose }: FormProps) {
  const setupQ = useQuery({ queryKey: ['acc-books', 'trade-setup'], queryFn: tradeSetup });
  const home = setupQ.data?.stateCode ?? null;
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const customers = ledgers.filter((l) => l.role === 'D');
  const salesLedgers = ledgers.filter((l) => l.group === 'Sales Accounts' && !/Return|Rate Difference/i.test(l.name));
  const usualLedger = (partyId: number | '') => {
    const p = partyId === '' ? undefined : byId.get(partyId);
    const intra = !(home && p?.stateCode && home !== p.stateCode);
    return salesLedgers.find((l) => l.name === (intra ? 'Sales – Local' : 'Sales – Interstate'))?.id ?? salesLedgers[0]?.id ?? '';
  };
  const today = ymd(new Date());
  const d = voucher?.details;

  const [s, setS] = useState<State>(() => (voucher && d
    ? {
      date: voucher.date, party: d.partyLedgerId, ledger: d.ledgerId, order: d.orderNo ?? '', orderDate: d.orderDate ?? '', days: d.creditDays, roundOff: d.roundOff,
      lines: fromItems(voucher.items), irn: d.irn ?? '', ack: d.ackNo ?? '', ewb: d.ewayBillNo ?? '', vehicle: d.vehicle ?? '', km: d.km ?? 0,
      narr: voucher.narration ?? '', pd: d.pd ?? {},
    }
    : { date: today, party: '', ledger: '', order: '', orderDate: '', days: 30, roundOff: true, lines: [newLine()], irn: '', ack: '', ewb: '', vehicle: '', km: 0, narr: '', pd: {} }));
  const set = (p: Partial<State>) => setS((x) => ({ ...x, ...p }));

  const party = s.party === '' ? undefined : byId.get(s.party);
  const ledger = s.ledger === '' ? undefined : byId.get(s.ledger);
  const intra = !(home && party?.stateCode && home !== party.stateCode);
  const c = tradeCalc('Sales', s.lines.map((l) => ({ qty: l.qty, rate: l.rate, disc: l.disc, gst: l.gst })), { intra, roundOff: s.roundOff });
  const no = voucher?.voucherNo ?? nextNo;
  const due = addDays(s.date, s.days);
  const ewbNeeded = c.R > 50000;
  const placeOfSupply = party?.stateCode ? `${GST_STATE_MAP[party.stateCode] ?? ''} (${party.stateCode})` : '—';

  // What stops the save, and what only warns — as the prototype checks.
  const block: string[] = [];
  const warn: string[] = [];
  const valid = s.lines.filter((l) => l.qty > 0 && l.rate > 0);
  if (!s.date) block.push('Enter the invoice date.');
  else if (s.date > today) block.push(`The date can’t be after today (${dd(today)}).`);
  if (!party) block.push('Choose the customer (Party A/c name).');
  if (!ledger) block.push('Choose the Sales ledger.');
  if (!valid.length) block.push('Add at least one item with quantity and rate.');
  if (valid.some((l) => !l.name.trim())) block.push('Every item needs its name.');
  if (valid.some((l) => !l.hsn.trim())) block.push('Every item needs an HSN code.');
  if (valid.some((l) => l.disc < 0 || l.disc > 100)) block.push('Discount must be between 0 and 100%.');
  if (s.orderDate && s.orderDate > s.date) block.push("Buyer's order date cannot be after the invoice date.");
  if (ewbNeeded && !s.vehicle.trim()) block.push('Invoice is above ₹50,000 — enter the vehicle no. for the e-way bill.');
  if (s.vehicle.trim() && !(s.km > 0)) block.push('Enter the distance (km) for the e-way bill.');
  if (party?.gstin && !s.irn.trim()) warn.push('IRN not entered — a B2B invoice must have an e-invoice before it goes to the customer. Enter it here once generated on the portal.');
  if (party && !party.gstin) warn.push(`${party.name} has no GSTIN — this is a B2C invoice.`);
  if (party && ledger && ledger.id !== usualLedger(s.party)) warn.push(`${party.name} is ${intra ? 'in' : 'outside'} ${GST_STATE_MAP[home ?? ''] ?? 'the company’s state'} — usually "${byId.get(usualLedger(s.party) as number)?.name}".`);
  // Credit limit: what they owe plus this invoice going over it (Tally warns the same way).
  if (party && party.creditLimit > 0) {
    const owed = r2(party.balance);
    if (owed + c.R > party.creditLimit) warn.push(`Credit limit of ${party.name} is ${inr(party.creditLimit)} — outstanding ${inr(owed)} + this invoice ${inr(c.R)} goes ${inr(r2(owed + c.R - party.creditLimit))} over it.`);
  }

  const post = [
    { l: party?.name ?? '', a: c.R, sub: [`New Ref ${no} · due ${dd(due)} · ${inr(c.R)}`] },
    { l: ledger?.name ?? '', a: -c.taxable }, { l: 'Output CGST', a: -c.cgst }, { l: 'Output SGST', a: -c.sgst }, { l: 'Output IGST', a: -c.igst }, { l: 'Round Off', a: -c.ro },
  ];
  const build = (): TradeInput => ({
    date: s.date, partyLedgerId: s.party as number, ledgerId: s.ledger as number, creditDays: s.days, roundOff: s.roundOff,
    items: valid.map(({ key: _k, ...l }) => ({ ...l, name: l.name.trim(), hsn: l.hsn.trim() })),
    orderNo: s.order.trim(), orderDate: s.orderDate || null, irn: s.irn.trim(), ackNo: s.ack.trim(), ewayBillNo: s.ewb.trim(),
    vehicle: s.vehicle.trim(), km: s.km, narration: s.narr.trim(), pd: s.pd,
  });
  const save = useSaveTrade('Sales', voucher, onClose);

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={<FormTitle title="Sales" no={no} chip={<ChipTag tone="g">Goods out</ChipTag>} />}
      footer={
        <FormFooter left={<>Invoice total <b>{inr(c.R)}</b></>} version={voucher ? voucher.version + 1 : undefined}
          saveLabel="Save Sales" blocked={block.length > 0} saving={save.isPending} onClose={onClose} onSave={() => save.mutate(build())} />
      }
    >
      <Grid>
        <F label={<>Invoice date <span className="font-semibold normal-case tracking-normal text-[var(--text-muted)]">· {dayName(s.date)}</span></>}>
          <Input type="date" value={s.date} max={today} onChange={(e) => set({ date: e.target.value })} />
        </F>
        <F label="Buyer's order no."><Input className="font-mono" maxLength={60} placeholder="optional" value={s.order} onChange={(e) => set({ order: e.target.value })} /></F>
        <F label="Order date"><Input type="date" max={s.date} value={s.orderDate} onChange={(e) => set({ orderDate: e.target.value })} /></F>
        <F label="Credit days (from invoice date)"><Input type="number" min={0} className="text-right font-mono" value={s.days} onChange={(e) => set({ days: Math.max(0, Math.round(Number(e.target.value) || 0)) })} /></F>
        <F label="Party A/c name *" className="col-span-2">
          <SearchableSelect
            options={customers.map((p) => ({ value: p.id, label: p.name, sublabel: [p.partyCode, p.gstin ?? 'No GSTIN'].filter(Boolean).join(' · ') }))}
            value={s.party} placeholder="Customer"
            onChange={(v) => { const id = v === '' ? '' : Number(v); set({ party: id, ledger: usualLedger(id) }); }}
          />
        </F>
        <F label="Cur Bal"><ReadBox value={curBal(party)} /></F>
        <F label="GSTIN · Place of supply"><Input disabled className="font-mono" value={party ? `${party.gstin ?? 'Unregistered'} · ${placeOfSupply}` : ''} /></F>
        <F label="Sales ledger *" className="col-span-2">
          <Select value={s.ledger} onChange={(e) => set({ ledger: Number(e.target.value) })}>
            {s.ledger === '' && <option value="">— choose —</option>}
            {salesLedgers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </F>
      </Grid>

      <Sec>Items</Sec>
      <ItemsTable
        lines={s.lines} amounts={c.lines.map((l) => l.amt)} setLines={(lines) => set({ lines })}
        taxRows={[['Output CGST', c.cgst], ['Output SGST', c.sgst], ['Output IGST', c.igst]]} ro={c.ro} total={c.R}
        rateOf={() => 0}
      />
      <label className="ml-1 mt-2 inline-flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
        <input type="checkbox" checked={s.roundOff} onChange={(e) => set({ roundOff: e.target.checked })} /> Round off the total to the rupee (Round Off ledger)
      </label>

      <PartyDetails title="Party Details — Buyer, Consignee & Dispatch" fields={PD_FIELDS} pd={s.pd} setPd={(pd) => set({ pd })} />

      <Sec>e-Invoice & e-Way Bill</Sec>
      <Grid>
        <F label="IRN (e-invoice)" className="col-span-2"><Input className="font-mono" maxLength={64} placeholder={party?.gstin ? 'From the e-invoice portal' : 'Not needed for B2C'} value={s.irn} onChange={(e) => set({ irn: e.target.value })} /></F>
        <F label="Ack no."><Input className="font-mono" maxLength={20} value={s.ack} onChange={(e) => set({ ack: e.target.value })} /></F>
        <F label="e-Way bill no."><Input className="font-mono" maxLength={15} value={s.ewb} onChange={(e) => set({ ewb: e.target.value })} /></F>
        <F label={`Vehicle no.${ewbNeeded ? ' *' : ''}`}>
          <Input className="font-mono" maxLength={20} placeholder={ewbNeeded ? 'e.g. HR45AB1234' : 'not needed ≤ ₹50,000'} value={s.vehicle} onChange={(e) => set({ vehicle: e.target.value.toUpperCase() })} />
        </F>
        <F label="Distance (km)"><Input type="number" min={0} className="text-right font-mono" value={s.km || ''} onChange={(e) => set({ km: Math.max(0, Math.round(Number(e.target.value) || 0)) })} /></F>
      </Grid>

      <Sec>Bill-wise Details{party ? ` for ${party.name}` : ''}</Sec>
      <NewRefTable name={no} billDate={s.date} due={due} days={s.days} amount={c.R} side="Dr" />

      <Msgs block={block} warn={warn} />
      <F label="Narration" className="mt-3">
        <Textarea rows={2} maxLength={1000} value={s.narr} placeholder={`Sales to ${party?.name ?? ''}`} onChange={(e) => set({ narr: e.target.value })} />
      </F>
      <Sec>Posting</Sec>
      <Posting lines={post} />
    </Modal>
  );
}
